import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';

enum ResponseErrorType {
  availabilityError = 'availabilityError',
  lowAvailabilityError = 'lowAvailabilityError',
  pricingError = 'pricingError'
}

type ResponseErrors = Record<keyof typeof ResponseErrorType, string | null>;

const defaultErrors: ResponseErrors = {
  availabilityError: null,
  lowAvailabilityError: null,
  pricingError: null
};

type ContextType = {
  responseErrors: ResponseErrors;
  nativeError?: Error;
  setNativeError: React.Dispatch<React.SetStateAction<Error | undefined>>;
  updateResponseErrors(_error: Partial<ResponseErrors>): void;
};

const defaultContext: ContextType = {
  responseErrors: defaultErrors,
  nativeError: undefined,
  setNativeError() {},
  updateResponseErrors(_error: Partial<ResponseErrors>) {}
};

const ErrorContext = createContext<ContextType>(defaultContext);

export function ErrorContextProvider({ children }) {
  const [nativeError, setNativeError] = useState<Error | undefined>(undefined);
  const [responseErrors, setErrors] = useState<ResponseErrors>(defaultErrors);

  const updateResponseErrors = useCallback(
    (error: Partial<ResponseErrors>) =>
      setErrors(prevErrors => ({ ...prevErrors, ...error })),
    []
  );

  const value = useMemo<ContextType>(
    (): ContextType => ({
      responseErrors,
      nativeError,
      setNativeError,
      updateResponseErrors
    }),
    [nativeError, responseErrors, updateResponseErrors]
  );

  return (
    <ErrorContext.Provider value={value}>{children}</ErrorContext.Provider>
  );
}

ErrorContextProvider.displayName = 'ErrorContextProvider';

export function useResponseErrors(): ResponseErrors {
  const { responseErrors } = useContext(ErrorContext);

  return responseErrors;
}

export function usePricingError(): string | null {
  const { pricingError } = useContext(ErrorContext).responseErrors;

  return pricingError;
}

export default ErrorContext;
